import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ErrorIcon from '@material-ui/icons/Error';
import { centsToDollar } from '../../utils/formatters';
import PaymentsTable from './PaymentsTable';
import NewPayment from './NewPayment';
import { LoanTypeMetricBreakdown } from '../../components';
import { AmountCountItem } from './CountAmountLineItem';

const styles = makeStyles((theme) => ({
    root: {},
    summaryContainer: {
        paddingLeft: '50px',
        paddingRight: '50px',
        width: '85vw'
    },
    summaryHeader: {
        textAlign: 'center'
    },
    summaryTotals: {
        paddingTop: '10px',
        paddingBottom: '10px'
    },
    summaryBody: {
        paddingTop: '10px',
        paddingBottom: '10px'
    },
    summary: {
        backgroundColor: theme.palette.grey.main,
        borderRadius: '5px',
        border: 'groove',
        borderColor: 'black',
        borderWidth: '0.5px',
    },
    table: {
        marginTop: '30px',
        width: '85vw',
        marginRight: '5vw'
    },
    subItem: {
        listStyleType: "circle",
        marginTop: '5px'
    },
    lenderTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '10px'
    },
    lenderTh: {
        border: 'none',
        padding: '4px',
        textAlign: 'center'
    },
    lenderThAmount: {
        border: 'none',
        padding: '4px',
        textAlign: 'center'
    },
    lenderTd: {
        border: 'none',
        paddingRight: '24px'
    },
    lenderTdAmount: {
        border: 'none',
        textAlign: 'right'
    },
    lenderTdCount: {
        border: 'none',
        textAlign: 'center',
        position: 'relative'
    },
    lenderTdStage: {
        border: 'none',
        paddingRight: '24px',
        paddingLeft: '32px'
    },
    lenderIcon: {
        position: 'absolute',
        right: '-18px',
        top: '13px',
        transform: 'translateY(-50%)',
        cursor: 'pointer'
    },
}));

const Payments = ({ dailyProcessingData, selectedDate}) => {
    const classes = styles();
    useTheme();
    const [showStagedRows, setShowStagedRows] = useState({});
    const [showNewPayment, setShowNewPayment] = useState(false);

    const handleToggleStagedRows = (lenderShortName) => {
        setShowStagedRows((prev) => ({
            ...prev,
            [lenderShortName]: !prev[lenderShortName]
        }));
    };

    if (dailyProcessingData === null) {
        return <h1>Loading...</h1>;
    }

    const paymentSummary = dailyProcessingData.paymentsSummary;
    const payments = dailyProcessingData.payments;
    const toggleShowNewPayment = () => setShowNewPayment(!showNewPayment);

    return (
        <Fragment>
            <Grid container direction="column">
                <Grid item className={classes.summaryContainer}>
                    <h3 className={classes.summaryHeader}>Summary</h3>
                    <Grid container direction="column" className={classes.summary}>
                        <Grid container justifyContent="space-evenly" className={classes.summaryBody}>
                            <Grid item className={classes.summaryTotals}>
                                <li>Total Payments: {paymentSummary.totalPaymentAmount ? centsToDollar(paymentSummary.totalPaymentAmount) : "$0"}</li>
                                <li>Total Payments Count: {paymentSummary.totalPaymentNumber || 0}</li>
                                <br />
                                <li>Total Thread Payments: {paymentSummary.totalThreadPaymentAmount ? centsToDollar(paymentSummary.totalThreadPaymentAmount) : "$0"}</li>
                                <li>Total Thread Payments Count: {paymentSummary.totalThreadPaymentNumber || 0}</li>
                                <br />
                                <li>Total WSFS Payments: {paymentSummary.totalWsfsPaymentAmount ? centsToDollar(paymentSummary.totalWsfsPaymentAmount) : "$0"}</li>
                                <li>Total WSFS Payments Count: {paymentSummary.totalWsfsPaymentNumber || 0}</li>
                                <br />
                                <li>Total Reprocessed Payments: {paymentSummary.totalReprocessedPaymentAmount ? centsToDollar(paymentSummary.totalReprocessedPaymentAmount) : "$0"}</li>
                                <li>Total Reprocessed Payments Count: {paymentSummary.totalReprocessedPaymentNumber || 0}</li>
                            </Grid>
                            <Grid item className={classes.summaryTotals}>
                                <Grid item>
                                    <LoanTypeMetricBreakdown loanType={'Solar'} totalStat={paymentSummary.totalPaymentAmountEsg || 0} countStat={paymentSummary.totalPaymentNumberEsg || 0}></LoanTypeMetricBreakdown>
                                </Grid>
                                <Grid item>
                                    <LoanTypeMetricBreakdown loanType={'Home Improvement'} totalStat={paymentSummary.totalPaymentAmountHi || 0} countStat={paymentSummary.totalPaymentNumberHi || 0}></LoanTypeMetricBreakdown>
                                </Grid>
                                <Grid item>
                                    <LoanTypeMetricBreakdown loanType={'Healthcare'} totalStat={paymentSummary.totalPaymentAmountHc || 0} countStat={paymentSummary.totalPaymentNumberHc || 0}></LoanTypeMetricBreakdown>
                                </Grid>
                            </Grid>
                            <Grid item className={classes.summaryTotals}>
                                <AmountCountItem label={"Paid Amount/Count"} amount={paymentSummary.paidAmount} count={paymentSummary.paidCount} boldLabel={false} />
                                <AmountCountItem label={"Processing Amount/Count"} amount={paymentSummary.processingAmount} count={paymentSummary.processingCount} boldLabel={false} />
                                <AmountCountItem label={"Held Amount/Count"} amount={paymentSummary.heldAmount} count={paymentSummary.heldCount} boldLabel={false} />
                                <AmountCountItem label={"Received Amount/Count"} amount={paymentSummary.receivedAmount} count={paymentSummary.receivedCount} boldLabel={false} />
                            </Grid>
                        </Grid>
                        <Divider />
                            <Grid item>
                                <Grid container spacing={2} justifyContent="space-evenly" direction="row" className={classes.summaryBody}>
                                    {paymentSummary.paymentsSummaryByLender ? paymentSummary.paymentsSummaryByLender.map((row) => (
                                    <Grid item>
                                        <p style={{ margin: 0 }}><b><u>{row.shortName}</u></b></p>
                                        <table className={classes.lenderTable}>
                                            <thead>
                                                <tr>
                                                    <th style={{ border: 'none' }}></th>
                                                    <th className={classes.lenderThAmount}>Amount</th>
                                                    <th className={classes.lenderTh}>Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td className={classes.lenderTd}><b>Total Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.totalPaymentAmount)}</td>
                                                    <td className={classes.lenderTdCount}>{row.totalPaymentNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td className={classes.lenderTd}><b>NSF Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.nsfAmountCents)}</td>
                                                    <td className={classes.lenderTdCount}>{row.nsfNumber}</td>
                                                </tr>
                                                <tr>
                                                    <td className={classes.lenderTd}><b>All Staged Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.stagedPaymentsAmountCents)}</td>
                                                    <td className={classes.lenderTdCount}>
                                                        {row.stagedPaymentsNumber}
                                                        <Tooltip title={showStagedRows[row.shortName] ? 'Hide stage breakdown' : 'Show stage breakdown'} placement="top">
                                                            <span className={classes.lenderIcon}
                                                                onClick={() => handleToggleStagedRows(row.shortName)}>
                                                                {showStagedRows[row.shortName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </span>
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                                {showStagedRows[row.shortName] && (
                                                    <>
                                                        <tr>
                                                            <td className={classes.lenderTdStage}><b>Stage 1</b></td>
                                                            <td className={classes.lenderTdAmount}>{centsToDollar(row.s1AmountCents)}</td>
                                                            <td className={classes.lenderTdCount}>{row.s1Number}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={classes.lenderTdStage}><b>Stage 2</b></td>
                                                            <td className={classes.lenderTdAmount}>{centsToDollar(row.s2AmountCents)}</td>
                                                            <td className={classes.lenderTdCount}>{row.s2Number}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={classes.lenderTdStage}><b>Stage 3</b></td>
                                                            <td className={classes.lenderTdAmount}>{centsToDollar(row.s3AmountCents)}</td>
                                                            <td className={classes.lenderTdCount}>{row.s3Number}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={classes.lenderTdStage}><b>Stage 4</b></td>
                                                            <td className={classes.lenderTdAmount}>{centsToDollar(row.s4AmountCents)}</td>
                                                            <td className={classes.lenderTdCount}>{row.s4Number}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className={classes.lenderTdStage}><b>Stage 5</b></td>
                                                            <td className={classes.lenderTdAmount}>{centsToDollar(row.s5AmountCents)}</td>
                                                            <td className={classes.lenderTdCount}>{row.s5Number}</td>
                                                        </tr>
                                                    </>
                                                )}
                                                <tr>
                                                    <td className={classes.lenderTd}><b>Thread Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.threadAffiliatePaymentAmount)}</td>
                                                    <td className={classes.lenderTdCount}>{row.threadAffiliatePaymentCount}</td>
                                                </tr>
                                                <tr>
                                                    <td className={classes.lenderTd}><b>WSFS Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.wsfsAffiliatePaymentAmount)}</td>
                                                    <td className={classes.lenderTdCount}>{row.wsfsAffiliatePaymentCount}</td>
                                                </tr>
                                                <tr>
                                                    <td className={classes.lenderTd}><b>Reprocessed Payments</b></td>
                                                    <td className={classes.lenderTdAmount}>{centsToDollar(row.reprocessedPaymentAmount || 0)}</td>
                                                    <td className={classes.lenderTdCount}>{row.reprocessedPaymentCount || 0}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </Grid>
                                    )) : null}
                                </Grid>
                            </Grid>
                        <Divider />
                        <Grid item >
                            <Grid container spacing={2} justifyContent="space-evenly" direction="row" className={classes.summaryBody} >
                                <Grid item>
                                    <p><b><u>Staged Funding</u></b></p>
                                    <li>Has Staged Funding: {paymentSummary.stagedPayments || 0}</li>
                                    <li>NO Staged Funding: {paymentSummary.unstagedPayments || 0}</li>
                                </Grid>
                                <Grid item>
                                    <p><b><u>Holds</u></b></p>
                                    <li>Loans coming off hold: {paymentSummary.comingOffHold || 0}</li>
                                </Grid>
                                <Grid item>
                                    <p><b><u>PP360 Payments</u></b></p>
                                    <li>Total Payments: {paymentSummary.totalPower360PaymentNumber || 0}</li>
                                    <li>Total Amount: {centsToDollar(paymentSummary.totalPower360PaymentAmount || 0)}</li>
                                </Grid>
                                {
                                    paymentSummary.totalBouncedPaymentNumber > 0
                                    ? <Grid item>
                                          <p><ErrorIcon color='error' /><b><u>Bounces</u></b></p>
                                          <li>Bounced Payments: {paymentSummary.totalBouncedPaymentNumber || 0}</li>
                                      </Grid>
                                    : null
                                }
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item className={classes.table}>
                    <PaymentsTable payments={payments} selectedDate={selectedDate} toggleShowNewPayment={toggleShowNewPayment} paymentType={'payments'} />
                </Grid>
            </Grid>
            <NewPayment showNewPayment={showNewPayment} toggleShowNewPayment={toggleShowNewPayment} selectedDate={selectedDate} />
        </Fragment>
    );
}

Payments.propTypes = {
    dailyProcessingData: PropTypes.shape({
        paymentsSummary: PropTypes.shape({
            totalPaymentAmount: PropTypes.number,
            totalPaymentNumber: PropTypes.number,
            totalThreadPaymentAmount: PropTypes.number,
            totalThreadPaymentNumber: PropTypes.number,
            totalWsfsPaymentAmount: PropTypes.number,
            totalWsfsPaymentNumber: PropTypes.number,
            totalReprocessedPaymentAmount: PropTypes.number,
            totalReprocessedPaymentNumber: PropTypes.number,
            totalPaymentAmountEsg: PropTypes.number,
            totalPaymentNumberEsg: PropTypes.number,
            totalPaymentAmountHi: PropTypes.number,
            totalPaymentNumberHi: PropTypes.number,
            totalPaymentAmountHc: PropTypes.number,
            totalPaymentNumberHc: PropTypes.number,
            paidAmount: PropTypes.number,
            paidCount: PropTypes.number,
            processingAmount: PropTypes.number,
            processingCount: PropTypes.number,
            heldAmount: PropTypes.number,
            heldCount: PropTypes.number,
            receivedAmount: PropTypes.number,
            receivedCount: PropTypes.number,
            paymentsSummaryByLender: PropTypes.arrayOf(PropTypes.shape({
                shortName: PropTypes.string,
                totalPaymentAmount: PropTypes.number,
                totalPaymentNumber: PropTypes.number,
                nsfAmountCents: PropTypes.number,
                nsfNumber: PropTypes.number,
                stagedPaymentsAmountCents: PropTypes.number,
                stagedPaymentsNumber: PropTypes.number,
                s1AmountCents: PropTypes.number,
                s1Number: PropTypes.number,
                s2AmountCents: PropTypes.number,
                s2Number: PropTypes.number,
                s3AmountCents: PropTypes.number,
                s3Number: PropTypes.number,
                s4AmountCents: PropTypes.number,
                s4Number: PropTypes.number,
                s5AmountCents: PropTypes.number,
                s5Number: PropTypes.number,
                threadAffiliatePaymentAmount: PropTypes.number,
                threadAffiliatePaymentCount: PropTypes.number,
                wsfsAffiliatePaymentAmount: PropTypes.number,
                wsfsAffiliatePaymentCount: PropTypes.number,
                reprocessedPaymentAmount: PropTypes.number,
                reprocessedPaymentCount: PropTypes.number
            })),
            stagedPayments: PropTypes.number,
            unstagedPayments: PropTypes.number,
            comingOffHold: PropTypes.number,
            totalBouncedPaymentNumber: PropTypes.number,
            totalPower360PaymentNumber: PropTypes.number,
            totalPower360PaymentAmount: PropTypes.number,
        }),
        payments: PropTypes.array
    }),
    selectedDate: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date)
    ])
};

export default Payments;
